import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Dialog, DialogContent } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  marketingErrorKey,
  useAutomationQuery,
} from "@/partner/entities/marketing";
import { DialogHeader } from "@/shared/components";
import {
  fromAutomation,
  type BroadcastFormState,
} from "../model/broadcastForm";
import { broadcastPollInterval } from "../model/broadcastPolling";
import { BroadcastEditor } from "./BroadcastEditor";

interface BroadcastDialogProps {
  id: string;
  onClose: () => void;
}

/**
 * Диалог рассылки на странице списка (?tab=edit&id=), fullScreen с
 * DialogHeader, как редактор письма. Пока рассылка отправляется, данные
 * перечитываются каждые 10 секунд, запланированная раз в минуту и чаще у
 * времени запуска (broadcastPollInterval). Форма монтируется с первым загруженным состоянием: опрос не
 * должен затирать правки. Закрытие с несохранёнными правками спрашивает
 * подтверждение, как EmailTemplateDialog.
 */
export const BroadcastDialog: FC<BroadcastDialogProps> = ({ id, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [pollInterval, setPollInterval] = useState(0);
  const { data, error } = useAutomationQuery({
    variables: { id },
    fetchPolicy: "network-only",
    pollInterval,
  });
  const automation = data?.automation;
  const nextInterval = broadcastPollInterval(
    automation?.state,
    automation?.scheduledAt,
    Date.now()
  );
  if (nextInterval !== pollInterval) setPollInterval(nextInterval);

  const [initialState, setInitialState] = useState<BroadcastFormState | null>(
    null
  );
  if (automation && !initialState) {
    setInitialState(fromAutomation(automation));
  }

  const dirtyRef = useRef(false);
  // Стабильная ссылка: иначе эффект формы вызывался бы на каждый рендер.
  const onDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const handleClose = () => {
    if (
      dirtyRef.current &&
      !window.confirm(t("shared.form.unsavedChangesWarning"))
    ) {
      return;
    }
    onClose();
  };

  // Ошибка загрузки: снекбар и закрыть диалог. Сбой очередного опроса при
  // уже загруженных данных диалог не закрывает.
  const loadFailed = Boolean(error) && !automation;
  const failureHandled = useRef(false);
  useEffect(() => {
    if (!loadFailed || failureHandled.current) return;
    failureHandled.current = true;
    enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" });
    onClose();
  }, [loadFailed, error, enqueueSnackbar, t, onClose]);

  return (
    <Dialog open onClose={handleClose} fullScreen disableEscapeKeyDown>
      {automation && initialState ? (
        <BroadcastEditor
          automation={automation}
          initialState={initialState}
          onClose={handleClose}
          onDirtyChange={onDirtyChange}
        />
      ) : (
        <>
          <DialogHeader
            title={t("broadcasts.edit.title")}
            loading
            onClose={handleClose}
          />
          <DialogContent sx={{ p: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};
